import { useCruiseStore, useDetailsStore, useFlightStore, usePriceStore } from '..'
import { Direction } from '../../../bewotec/openapi'
import { COMFORTAI, IND, INDAI } from '../../../general/master-data'
import { ServiceIncluded } from '../flight/interfaces'
import { DisplayFunctionFlightPrice } from '../price/interfaces'
import { IFetchRailOption } from '../rail/interfaces'
import { flightHelper, makeFlightEntry } from './helper.js'
import { IExpinetSelectedFlight, InOutBoundItem, SelectableRailOptionsFlight } from './interfaces'

export const getGateway = (): string => {
  const flightStore = useFlightStore()
  const selectedFlight = flightStore.selectedFlight

  if (!selectedFlight) return ''

  const outboundFlight = selectedFlight.outboundFlight
  const inboundFlight = selectedFlight.inboundFlight

  switch (selectedFlight.direction) {
    case Direction.RT:
    case Direction.OJ:
      return outboundFlight?.departure.airportId ?? ''
    case Direction.R:
      return inboundFlight?.arrival.airportId ?? ''
    case Direction.O:
      return outboundFlight?.departure.airportId ?? ''
    default:
      return ''
  }
}

export const flightItems = () => {
  const detailsStore = useDetailsStore()
  const priceStore = usePriceStore()
  const flightStore = useFlightStore()

  const items: InOutBoundItem[] = []
  let offset = 0

  if (!detailsStore.mandatoryFlightForPriceModel) {
    items.push({
      key: String(offset++),
      listDisplay: 'Flug wählen',
    })
  }

  const displayFlightPrice: DisplayFunctionFlightPrice = priceStore.flight
  const aidaFlights = flightStore.aidaFlights

  const DIRECTIONS = [Direction.RT, Direction.OJ, Direction.O, Direction.R]
  const DIVIDERS = ['HIN- und RÜCKFLUG', 'GABELFLUG', 'nur HINFLUG', 'nur RÜCKFLUG']

  for (let i = 0; i < DIRECTIONS.length; ++i) {
    const direction = DIRECTIONS[i]
    const dividerLabel = DIVIDERS[i]

    items.push({
      key: String(offset++),
      listDisplay: dividerLabel,
      divider: true,
    })

    let j = 0

    if (!!aidaFlights?.[direction].length) {
      for (; j < aidaFlights[direction].length; ++j) {
        const flight = aidaFlights[direction][j]
        items.push({
          key: String(j + offset),
          ...makeFlightEntry(flight, displayFlightPrice),
        })
      }
    } else {
      items.push({
        key: String(j++),
        listDisplay: 'Nicht verfügbar',
        unavailable: true,
      })
    }

    offset += j
  }

  return items
}

export const selectedFlightKey = (): string => {
  const flightStore = useFlightStore()

  const flight: IExpinetSelectedFlight | null = flightStore.selectedFlight
  const flightItems: InOutBoundItem[] = flightStore.flightItems

  if (!flight) {
    return '0'
  }
  for (let i = 1; i < flightItems.length; ++i) {
    const item = flightItems[i]
    if ('flight' in item && item.flight.outboundFlight && flightHelper.flightsAreEqual(item.flight.outboundFlight, flight)) {
      return String(i)
    } else if ('flight' in item && item.flight.inboundFlight && flightHelper.flightsAreEqual(item.flight.inboundFlight, flight)) {
      return String(i)
    }
  }
  return String(0)
}

export const serviceIncluded = (): ServiceIncluded => {
  const detailsStore = useDetailsStore()
  const cruiseStore = useCruiseStore()
  const flightStore = useFlightStore()

  const priceModel = detailsStore.priceModel!
  const route = cruiseStore.route

  const NBSP = '\u00a0'
  const text = `${NBSP}|${NBSP}inkl. Rail&Fly Ticket`
  const isSelectedRailOption = !!flightStore.selectedRailOption && 'railClassCode' in flightStore.selectedRailOption

  const isClassValidForPriceModel =
    ([IND, INDAI].includes(priceModel) &&
      isSelectedRailOption &&
      (flightStore.selectedRailOption as IFetchRailOption).railClassCode === '2') ||
    ([COMFORTAI].includes(priceModel) &&
      isSelectedRailOption &&
      (flightStore.selectedRailOption as IFetchRailOption)?.railClassCode === '1')

  const railDirection = flightStore.railDirectionRNF
  const isRailDirectionSameAsOutboundFlightDirection = railDirection === 'both' || railDirection === 'outbound'
  const isRailDirectionSameAsInboundFlightDirection = railDirection === 'both' || railDirection === 'inbound'

  return {
    outbound:
      !route?.germanDeparture && isClassValidForPriceModel && isRailDirectionSameAsOutboundFlightDirection ? text : '',
    inbound:
      !route?.germanArrival && isClassValidForPriceModel && isRailDirectionSameAsInboundFlightDirection ? text : '',
  }
}

export const isNoRailAllowed = (): boolean => {
  const cruiseStore = useCruiseStore()
  const detailsStore = useDetailsStore()
  const flightStore = useFlightStore()

  const route = cruiseStore.route
  const isPremiumOrComfortAI = [COMFORTAI, IND, INDAI].includes(detailsStore.priceModel!)
  const hasGermanDeparture = !!route?.germanDeparture
  const hasGermanArrival = !!route?.germanArrival
  const selectableRailOptions = flightStore.selectableRailOptions
  const isRailAvailableForDirection =
    !('error' in selectableRailOptions) &&
    ((!hasGermanDeparture && !!selectableRailOptions[Direction.O]?.length) ||
      (!hasGermanArrival && !!selectableRailOptions[Direction.R]?.length))

  return !(isPremiumOrComfortAI && isRailAvailableForDirection)
}

export const getRailErrorMessage = () => {
  const cruiseStore = useCruiseStore()
  const flightStore = useFlightStore()

  const railOptions: SelectableRailOptionsFlight = flightStore.selectableRailOptions
  const isError = 'error' in railOptions
  const noAvailabilities = isError|| (!railOptions?.[Direction.RT] && !railOptions?.[Direction.R] && !railOptions?.[Direction.O])
  const route = cruiseStore.route
  const hasGermanDeparture = !!route?.germanDeparture
  const hasGermanArrival = !!route?.germanArrival

  if (hasGermanDeparture && hasGermanArrival) {
    return 'Eine Bahnfahrt ist bei deutschen Start-/Endhäfen nicht möglich'
  }

  if (!!flightStore.selectedFlight && noAvailabilities) {
    return 'Momentan keine Bahnoptionen verfügbar.'
  }

  if (flightStore.railDirectionRNF !== 'none' && isError) {
    return 'Leider ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut...'
  }

  return ''
}

export default {
  getGateway,
  flightItems,
  selectedFlightKey,
  serviceIncluded,
  isNoRailAllowed,
  getRailErrorMessage,
}
